import React, { memo, useState } from "react";

import { isEqual } from "lodash/fp";

import RemoveTopicReportConfirmationModal from "@app/analytics/components/reports/RemoveTopicReportConfirmationModal";
import TableCell from "@app/analytics/components/Table/TableCell";
import { CustomTag } from "@app/analytics/components/Tag";
import useRemoveTopicReport from "@app/analytics/hooks/topics/useRemoveTopicReport";
import ReportRowContextMenu from "@app/analytics/MeetingReports/components/ReportRowContextMenu";
import ReportActionsCell from "@app/analytics/MeetingReports/ReportsTable/cells/ReportActionsCell";
import SourceCell from "@app/analytics/MeetingReports/ReportsTable/cells/SourceCell";
import { ReportTableRow } from "@app/analytics/MeetingReports/ReportsTable/styles";
import {
  getFormattedDateRange,
  isMenuCloseEvent,
} from "@app/analytics/MeetingReports/ReportsTable/utils";
import type { OnOpenReportHandler } from "@app/analytics/MeetingReports/types";
import type { Topic } from "@app/analytics/types";
import useAuth from "@app/common/auth/useAuth";
import { toast } from "@app/common/hooks/useToasts";
import useContextMenu from "@app/utils/hooks/useContextMenu";

import TopicReportProcessingRow from "./TopicReportProcessingRow";
import TopicTitleCell from "./TopicTitleCell";
import OwnerCell from "../cells/OwnerCell";

interface TopicReportTableRowProps {
  report: Topic;
  isUnseen?: boolean;
  onOpenReport: OnOpenReportHandler;
  /* Whether the current row needs to account for the "Read Score" column */
  withScore: boolean;
}

const RemoveConfirmationModal = ({
  report,
  onClose,
}: {
  report: Topic;
  onClose: () => void;
}) => {
  const { removeTopicReport, isRemoving } = useRemoveTopicReport();

  const handleConfirmRemove = () => {
    return removeTopicReport(
      {
        topicId: report.id,
        ownerUserId: report.ownerUserId,
      },
      {
        onError: () => {
          toast.error(
            "Unable to remove report. Please refresh the page or try again."
          );
        },
        onSettled: onClose,
      }
    );
  };

  return (
    <RemoveTopicReportConfirmationModal
      open
      onClose={onClose}
      onConfirm={handleConfirmRemove}
      loading={isRemoving}
    />
  );
};

const TopicReportTableRow = ({
  report,
  isUnseen = false,
  onOpenReport,
  withScore,
}: TopicReportTableRowProps) => {
  const [confirmRemoveReport, setConfirmRemoveReport] = useState<Topic | null>(
    null
  );

  const { user } = useAuth();

  const { handleContextMenuOpen, handleContextMenuClose, contextMenu } =
    useContextMenu();

  if (report.status === "processing") {
    return <TopicReportProcessingRow report={report} withScore={withScore} />;
  }

  const handleClickRow = (
    e: React.MouseEvent<HTMLElement>,
    openNewTab = false
  ) => {
    if (isMenuCloseEvent(e)) return;

    const newTab =
      openNewTab || e.button == 1 || e.buttons == 4 || e.ctrlKey || e.metaKey;
    onOpenReport(report.id, {
      newTab,
      type: "topic",
      ownerUserId: report.ownerUserId,
    });
  };

  const canEditReport = user?.id == report.ownerUserId;

  const handleOpenNewTabClick = (e: React.MouseEvent<HTMLElement>) => {
    handleContextMenuClose();
    handleClickRow(e, true);
  };

  return (
    <>
      <ReportTableRow
        data-test-id="meeting-report-table-row"
        data-tid={report.id}
        onClick={handleClickRow}
        onContextMenu={handleContextMenuOpen}
        onAuxClick={handleClickRow}
        unseen={isUnseen}
      >
        <SourceCell report={report} />
        <TopicTitleCell report={report} isEditable={canEditReport} />
        <TableCell sx={{ color: "shades.neutral.700" }}>
          {getFormattedDateRange(report)}
        </TableCell>
        {withScore && (
          <TableCell>{/* Placeholder for Read Score cell */}</TableCell>
        )}
        <TableCell>
          <CustomTag
            autoGenerated
            backgroundColor="avatar.indigo.dark"
            color="#FFF"
          >
            Readout
          </CustomTag>
        </TableCell>
        <OwnerCell owner={report.ownerName} />
        <ReportActionsCell
          report={report}
          canEditReport={canEditReport}
          onRemoveReport={() => setConfirmRemoveReport(report)}
        />
      </ReportTableRow>

      {confirmRemoveReport != null && (
        <RemoveConfirmationModal
          report={confirmRemoveReport}
          onClose={() => setConfirmRemoveReport(null)}
        />
      )}
      <ReportRowContextMenu
        handleContextMenuClose={handleContextMenuClose}
        contextMenuState={contextMenu}
        handleOpenNewTabClick={handleOpenNewTabClick}
      />
    </>
  );
};

const MemoizedTopicReportTableRow = memo(TopicReportTableRow, isEqual);

MemoizedTopicReportTableRow.displayName = "TopicReportTableRow";

export default MemoizedTopicReportTableRow;
